import { useDispatch } from 'react-redux';

import { DEVICE_UUID } from 'my-core/analytics/analytics-constants';
import { useStandardApiRequest } from 'my-core/hooks';
import { isMobileDevice } from 'my-core/user-agent-utils';
import { pluralize } from 'my-utils';

import Player from 'my-components/VideoPlayer';
import ErrorMessage from 'my-elements/ErrorMessage';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { faFaceFrown, faHand } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { toggleContactUsForm } from 'my-actions/AppStateActions';
import { fetchStudySessionRecording } from 'my-actions/StudySessionActions';

export default function StudySessionVideoPlayer({
  attachment,
  onClose,
  open,
  studySession,
  studySessionUser,
  watermark,
}) {
  const fetchKey = `fetch_ssu_recording_${studySessionUser.id}_${attachment.id}`;
  const { performRequest: performFetch, requestStatus: fetchStatus } = useStandardApiRequest({
    actionCreator: fetchStudySessionRecording,
    requestKey: fetchKey,
  });
  const dispatch = useDispatch();

  const deviceCheckRequired = !!(
    !fetchStatus?.success &&
    studySession.price &&
    !studySessionUser.device_usages?.some(u => u.uuid === DEVICE_UUID)
  );
  const maxDevicesReached =
    deviceCheckRequired && studySessionUser.device_usages?.length >= studySessionUser.device_limit;
  return (
    <Dialog
      disableAutoFocus
      onClose={fetchStatus?.fetching ? undefined : onClose}
      open={open}
      PaperComponent="div"
      sx={{
        '& .MuiDialog-paper': {
          width: deviceCheckRequired ? 'unset' : '100%',
          maxWidth: { xs: '90%', md: '75%' },
          borderRadius: 1,
          backgroundColor: 'background.paper',
        },
      }}
    >
      {renderContents()}
    </Dialog>
  );

  function renderContents() {
    if (!deviceCheckRequired) {
      return (
        <Player
          autoPlay
          disableFullscreen={isMobileDevice()} // so mobile viewers cannot view without watermark
          elements={attachment.video_elements}
          title={attachment.name}
          trackingProps={{
            video_id: 'study_session_recording',
            study_session_id: studySession.id,
            filename: attachment.name,
          }}
          urls={attachment.urls}
          videoDrmId={`AS:${attachment.id}`}
          watermark={watermark}
        />
      );
    }
    if (deviceCheckRequired) {
      const remainingDevices = studySessionUser.device_limit - studySessionUser.device_usages.length;
      return (
        <div
          css={({ palette, spacing }) => ({
            margin: '0 auto',
            padding: spacing(6, 4, 4),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spacing(1),
            maxWidth: 500,
            '& svg': { color: palette.text.secondary },
            '& .MuiDivider-root': { width: '100%', margin: spacing(2, 0) },
            '& .actions': { width: '100%', display: 'flex', justifyContent: 'flex-end', gap: spacing(1) },
          })}
        >
          {fetchStatus?.error || maxDevicesReached ?
            <>
              <FontAwesomeIcon icon={faFaceFrown} size="2x" />
              {maxDevicesReached ?
                <>
                  <Typography align="center" variant="h6">
                    Recording Device Limit Reached
                  </Typography>
                  <Divider />
                  <Typography variant="body3">{`Recordings can only be viewed on ${pluralize(
                    studySessionUser.device_limit,
                    'device',
                  )}. It looks like you have already viewed recordings for this session on ${pluralize(
                    studySessionUser.device_usages.length,
                    'other device',
                  )}`}</Typography>
                </>
              : <ErrorMessage align="center" error={fetchStatus.error} variant="h6" />}
              <Divider />
              <Typography>
                Need help with your issue?{' '}
                <MuiLink
                  component="button"
                  onClick={() => dispatch(toggleContactUsForm('I cannot access my live-prep recording'))}
                >
                  Contact Support
                </MuiLink>
              </Typography>
            </>
          : <>
              <FontAwesomeIcon icon={faHand} size="2x" />
              <Typography align="center" variant="h6">
                Authorize This Device to Watch Recording
              </Typography>
              <Divider />
              <Typography variant="body3">{`Recordings can only be viewed on ${pluralize(
                studySessionUser.device_limit,
                'unique device',
              )}. If you authorize this device, ${
                remainingDevices > 1 ?
                  `you will only be able to authorize ${pluralize(remainingDevices - 1, 'additional device')}`
                : 'you cannot view from any new devices'
              }. Are you sure you’d like to continue?`}</Typography>
              <Divider />
              <div className="actions">
                <Button disabled={!!fetchStatus?.fetching} onClick={onClose} variant="outlined">
                  Don&apos;t Authorize
                </Button>
                <Button
                  disabled={!!fetchStatus?.fetching}
                  onClick={() => performFetch(studySession.id, attachment.id, DEVICE_UUID, fetchKey)}
                  variant="contained"
                >
                  Authorize This Device
                </Button>
              </div>
            </>
          }
        </div>
      );
    }
  }
}
